import type { EntityDto, TreeRelationDto } from "api/types";

import { NODE_HEIGHT, NODE_WIDTH, RING_SLOT, RING_SPACING } from "./treeGraph.constants";
import type { RingGuide } from "./treeGraph.types";

type Point = { x: number; y: number };

export type RadialLayout = {
	positions: Map<string, Point>;
	/** Decorative circles between rings, each dotted where its ring's spokes cross it. */
	guides: RingGuide[];
};

/** Entity referenced by the most relations; used as the radial center when nothing is selected. */
export const pickDefaultCenter = (entities: EntityDto[], relations: TreeRelationDto[]): string | null => {
	if (entities.length === 0) {
		return null;
	}

	const counts = new Map<string, number>();
	relations.forEach((relation: TreeRelationDto) => {
		counts.set(relation.fromEntityId, (counts.get(relation.fromEntityId) ?? 0) + 1);
		counts.set(relation.toEntityId, (counts.get(relation.toEntityId) ?? 0) + 1);
	});

	let bestId = entities[0].id;
	let bestCount = counts.get(bestId) ?? 0;
	entities.forEach((entity: EntityDto) => {
		const count = counts.get(entity.id) ?? 0;
		if (count > bestCount) {
			bestId = entity.id;
			bestCount = count;
		}
	});

	return bestId;
};

/** Orders ring members so romantic partners end up in adjacent slots. */
const seatCouplesAdjacent = (ids: string[], romantic: Map<string, string[]>): string[] => {
	const ordered: string[] = [];
	const position = new Map<string, number>();

	ids.forEach((id: string) => {
		const placedPartners = (romantic.get(id) ?? [])
			.map((partnerId: string) => position.get(partnerId))
			.filter((index: number | undefined): index is number => index !== undefined);

		const insertAt = placedPartners.length > 0 ? Math.min(...placedPartners) + 1 : ordered.length;
		ordered.splice(insertAt, 0, id);
		ordered.forEach((placed: string, index: number) => position.set(placed, index));
	});

	return ordered;
};

/**
 * Ego-network radial layout: the center sits at the origin and every other entity lands on a
 * concentric ring at its BFS distance from the center (unreachable entities share one outermost
 * ring). The ring around the center is read by generation — ancestors ride the top of the ring,
 * descendants the bottom and same-generation peers the sides — so ancestry runs the same way as in
 * the hierarchical view. Further out, nodes inherit their BFS parent's angle so branches stay in the
 * same sector, couples sit adjacent, and a final sweep enforces a minimum angular gap so nodes on a
 * ring never overlap.
 */
export const computeRadialPositions = (
	entities: EntityDto[],
	adjacency: Map<string, string[]>,
	centerId: string,
	romantic: Map<string, string[]>,
	levelById: Map<string, number>
): RadialLayout => {
	// Breadth-first distances and discovery parents from the center over every relation kind.
	const distance = new Map<string, number>();
	const bfsParent = new Map<string, string>();
	distance.set(centerId, 0);
	const queue: string[] = [centerId];
	for (let head = 0; head < queue.length; head += 1) {
		const currentId = queue[head];
		(adjacency.get(currentId) ?? []).forEach((neighborId: string) => {
			if (!distance.has(neighborId)) {
				distance.set(neighborId, distance.get(currentId)! + 1);
				bfsParent.set(neighborId, currentId);
				queue.push(neighborId);
			}
		});
	}

	// Entities the BFS never reached share one ring beyond the farthest connected ring.
	let maxDistance = 0;
	entities.forEach((entity: EntityDto) => {
		maxDistance = Math.max(maxDistance, distance.get(entity.id) ?? 0);
	});
	const disconnectedRing = maxDistance + 1;

	const rings = new Map<number, string[]>();
	entities.forEach((entity: EntityDto) => {
		if (entity.id === centerId) {
			return;
		}

		const ring = distance.get(entity.id) ?? disconnectedRing;
		if (!rings.has(ring)) {
			rings.set(ring, []);
		}
		rings.get(ring)!.push(entity.id);
	});

	const centerLevel = levelById.get(centerId) ?? 0;
	/** -1 for an ancestor of the center's generation, 1 for a descendant, 0 for a peer. */
	const generationSide = (id: string): number => Math.sign((levelById.get(id) ?? centerLevel) - centerLevel);
	// Rings that group by generation (see layoutAnchoredRing); the rest inherit their parent's sector.
	const isAnchoredRing = (ring: number): boolean => ring === 1 || !bfsParent.has(rings.get(ring)![0]);

	// Ring radii grow by at least RING_SPACING and enough for every member to claim one arc slot.
	// Anchored rings need that room within a single half of the ring, since ancestors and descendants
	// never share a side.
	const ringIndexes = Array.from(rings.keys()).sort((a: number, b: number) => a - b);
	const radiusByRing = new Map<number, number>();
	let previousRadius = 0;
	ringIndexes.forEach((ring: number) => {
		const members = rings.get(ring)!;
		const circumferenceRadius = (members.length * RING_SLOT) / (2 * Math.PI);
		let halfRingRadius = 0;
		if (isAnchoredRing(ring)) {
			const bySide = new Map<number, number>();
			members.forEach((id: string) => {
				const side = generationSide(id);
				bySide.set(side, (bySide.get(side) ?? 0) + 1);
			});
			// Peers split across two sides; ancestors and descendants each own one half of the ring.
			const busiestSide = Math.max(bySide.get(-1) ?? 0, bySide.get(1) ?? 0, Math.ceil((bySide.get(0) ?? 0) / 2));
			halfRingRadius = (busiestSide * RING_SLOT) / Math.PI;
		}

		const radius = Math.max(previousRadius + RING_SPACING, circumferenceRadius, halfRingRadius);
		radiusByRing.set(ring, radius);
		previousRadius = radius;
	});

	const angleById = new Map<string, number>();

	// Sweep in angular order enforcing the minimum gap; fall back to an even spread if the arc overflows.
	const resolveRing = (ids: string[], preferred: Map<string, number>, minGap: number): void => {
		const sorted = [...ids].sort((a: string, b: string) => preferred.get(a)! - preferred.get(b)!);
		const finalAngles: number[] = [];
		sorted.forEach((id: string, index: number) => {
			const wanted = preferred.get(id)!;
			finalAngles.push(index === 0 ? wanted : Math.max(wanted, finalAngles[index - 1] + minGap));
		});

		const span = finalAngles[finalAngles.length - 1] - finalAngles[0];
		if (sorted.length > 1 && span > 2 * Math.PI - minGap) {
			const step = (2 * Math.PI) / sorted.length;
			sorted.forEach((id: string, index: number) => angleById.set(id, finalAngles[0] + index * step));
		} else {
			sorted.forEach((id: string, index: number) => angleById.set(id, finalAngles[index]));
		}
	};

	// Rings without placed parents (the center's own relations, disconnected leftovers) group by
	// generation: ancestors fan around 12 o'clock, descendants around 6 o'clock, and peers take the
	// sides — half of them at 3 o'clock, the rest at 9 o'clock.
	const layoutAnchoredRing = (ring: number, ids: string[]): void => {
		const minGap = RING_SLOT / radiusByRing.get(ring)!;
		const ordered = seatCouplesAdjacent(ids, romantic);
		const peers = ordered.filter((id: string) => generationSide(id) === 0);
		const rightSideCount = Math.ceil(peers.length / 2);
		const peerIndex = new Map<string, number>(peers.map((id: string, index: number) => [id, index]));

		const byAnchor = new Map<number, string[]>();
		ordered.forEach((id: string) => {
			const side = generationSide(id);
			let anchor = side < 0 ? -Math.PI / 2 : Math.PI / 2;
			if (side === 0) {
				anchor = peerIndex.get(id)! < rightSideCount ? 0 : Math.PI;
			}

			if (!byAnchor.has(anchor)) {
				byAnchor.set(anchor, []);
			}
			byAnchor.get(anchor)!.push(id);
		});

		const preferred = new Map<string, number>();
		byAnchor.forEach((members: string[], anchor: number) => {
			members.forEach((id: string, index: number) => {
				preferred.set(id, anchor + (index - (members.length - 1) / 2) * minGap);
			});
		});

		resolveRing(ids, preferred, minGap);
	};

	// Outer rings: members prefer their BFS parent's angle (sector inheritance) with siblings fanned around it.
	const layoutSectorRing = (ring: number, ids: string[]): void => {
		const minGap = RING_SLOT / radiusByRing.get(ring)!;

		const byParent = new Map<string, string[]>();
		ids.forEach((id: string) => {
			const parentId = bfsParent.get(id)!;
			if (!byParent.has(parentId)) {
				byParent.set(parentId, []);
			}
			byParent.get(parentId)!.push(id);
		});

		const preferred = new Map<string, number>();
		byParent.forEach((members: string[], parentId: string) => {
			const base = angleById.get(parentId) ?? -Math.PI / 2;
			members.forEach((id: string, index: number) => {
				preferred.set(id, base + (index - (members.length - 1) / 2) * minGap);
			});
		});

		// Same-ring couples: pull the later partner right next to the earlier one.
		const seen = new Set<string>();
		ids.forEach((id: string) => {
			const partnerId = (romantic.get(id) ?? []).find((candidate: string) => seen.has(candidate));
			if (partnerId !== undefined && preferred.has(partnerId)) {
				preferred.set(id, preferred.get(partnerId)! + minGap / 2);
			}
			seen.add(id);
		});

		resolveRing(ids, preferred, minGap);
	};

	ringIndexes.forEach((ring: number) => {
		const ids = rings.get(ring)!;
		if (isAnchoredRing(ring)) {
			layoutAnchoredRing(ring, ids);
		} else {
			layoutSectorRing(ring, ids);
		}
	});

	// xyflow positions are top-left corners, so every point shifts back by half a node.
	const positions = new Map<string, Point>();
	positions.set(centerId, { x: -NODE_WIDTH / 2, y: -NODE_HEIGHT / 2 });
	entities.forEach((entity: EntityDto) => {
		if (entity.id === centerId) {
			return;
		}

		const ring = distance.get(entity.id) ?? disconnectedRing;
		const radius = radiusByRing.get(ring)!;
		const angle = angleById.get(entity.id) ?? -Math.PI / 2;
		positions.set(entity.id, {
			x: radius * Math.cos(angle) - NODE_WIDTH / 2,
			y: radius * Math.sin(angle) - NODE_HEIGHT / 2
		});
	});

	// One decorative circle per ring, halfway inside it, dotted where the ring's spokes cross.
	const guides: RingGuide[] = ringIndexes.map((ring: number) => ({
		radius: radiusByRing.get(ring)! - RING_SPACING / 2,
		dotAngles: rings.get(ring)!.map((id: string) => angleById.get(id) ?? -Math.PI / 2)
	}));

	return { positions, guides };
};
